import { getGlobalWindow } from '../index.js'
import { isWindows } from '../platform.js'
import { signObj } from './SignMessenger.js'
import { ElMessageBox } from 'element-plus'

type CheckOptions = {
  /** 静默检验, 不弹出提示框, 在 check 失败时
   * @default false
   */
  silent?: boolean
  userId?: string
}

async function handleMessage(msg: string) {
  await ElMessageBox.confirm(msg, '系统提示', {
    type: 'warning',
    showCancelButton: false,
  })

  if (window.parent)
    return window.parent.location.replace('/merchant-platform/#/account/index')
  window.open('/merchant-platform/micro-account/info/index')
}

/**
 * Check the security component installation status and retrieve certificate information
 * 检查安全控件安装状态并获取证书信息
 *
 * @returns {Promise<{version: string, serialNo: string}>} Returns version and certificate serial number if successful
 * @throws {Error} Throws error if browser compatibility check fails, component initialization fails,
 *                 or if unable to retrieve version/serial number
 */
export async function check(options: CheckOptions) {
  const { silent = false, userId = undefined } = options

  if (!isWindows()) {
    if (!silent) {
      ElMessageBox.confirm('暂不支持MacOS浏览器安全组件！', '系统提示', {
        type: 'warning',
        showCancelButton: false,
      })
    }

    return Promise.reject(new Error('暂不支持MacOS浏览器安全组件！'))
  }

  // Check browser compatibility
  // 检查浏览器兼容性
  if (!signObj.checkOsBrowser())
    return Promise.reject(new Error('浏览器版本过低，暂不支持网银安全组件！'))

  // Initialize the core crypto provider
  // 初始化加密核心组件
  const NotInstallMsg = '检测到您未安装安全组件，请前往【账户中心】-【安全设置】-【下载安装包】'
  try {
    const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
    if (!getProviderRes) {
      if (!silent)
        handleMessage(NotInstallMsg)
      return Promise.reject(new Error(NotInstallMsg))
    }
  }
  catch {
    if (!silent)
      handleMessage(NotInstallMsg)
  }

  // Get security component version
  // 获取安全控件版本号
  if (userId)
    signObj.initContr(userId)
  const version = signObj.getLibVersion()

  // Get certificate serial number
  // 获取证书序列号
  const serialNo = await new Promise((resolve, reject) => {
    signObj.getSerialNoYB((result: string | number) => {
      if (result === '-1' || result === -1) {
        if (!silent)
          handleMessage('检测到您暂未安装证书，请前往【账户中心】-【安全设置】-【安装证书】')
        reject(new Error('未安装证书'))
      }
      resolve(result)
    })
  })
  // Return successful result with version and serial number
  // 返回成功结果，包含版本号和序列号
  return Promise.resolve({
    version,
    serialNo,
  })
}

/**
 * Sign a message using the security component
 * 使用安全控件对消息进行签名
 *
 * @param {string} pin - PIN code for signing (optional)
 * @param {string} msg - Message to be signed
 * @returns {Promise<{strSigned: string}>} Returns the signed message string
 * @throws {Error} Throws error if message is empty
 */
export async function sign(pin = '', msg: string) {
  // Validate input message
  // 验证输入消息
  if (!msg)
    return Promise.reject(new Error('请输入签名原文'))

  // Sign the message using xtSign method
  // 使用xtSign方法对消息进行签名
  const strSigned = await new Promise((resolve) => {
    signObj.xtSignYB(pin, msg, (res) => {
      resolve(res)
    })
  })

  // Return the signed message
  // 返回签名后的消息
  return Promise.resolve({
    strSigned,
  })
}

/**
 * 获取设备的MAC地址和IP地址
 * @returns Promise 返回一个Promise对象
 * @property {string} macAddress - 设备的MAC地址
 * @property {string} ipAddress - 设备的IP地址
 * @throws {Error} 当获取地址失败时抛出错误
 * @example
 * try {
 *   const { macAddress, ipAddress } = await getMacAddress();
 *   console.log(macAddress, ipAddress);
 * } catch (error) {
 *   console.error(error);
 * }
 */
export async function getMacAddress(): Promise<{ macAddress: string; ipAddress: string }> {
  const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
  if (!getProviderRes)
    throw new Error('未安装安全组件')

  // ?获取mac和IP
  const macWithIp = signObj.getMacAddress()
  const macAddress = macWithIp?.split(':')[0]
  const ipAddress = macWithIp?.split(':')[1] || macWithIp?.split(':')[0]

  return {
    macAddress,
    ipAddress,
  }
}

/**
 * 获取本机安装的 证书序列号
 * 用于国密签名场景，仅在 Windows 系统下有效
 *
 * @param operatorId 操作员ID，用于初始化证书容器
 * @returns Promise<string | false>
 *   - 成功: 返回证书序列号字符串
 *   - 失败: 返回 false（非 Windows 系统、无操作员ID、未安装安全组件、获取失败等情况）
 *
 * @example
 * ```typescript
 * const serialNo = await getLocalSignSerialNo('operator123')
 * if (serialNo) {
 *   console.log('证书序列号:', serialNo)
 *   localStorage.setItem('serialNo', serialNo)
 * } else {
 *   console.log('未安装证书或获取失败')
 * }
 * ```
 */
export async function getLocalSignSerialNo(operatorId: string): Promise<string | false> {
  const globalWindow = getGlobalWindow()

  if ((import.meta as any).env.MODE !== 'production') {
    console.warn('getLocalSignSerialNo is start')
    console.warn('operatorId is', operatorId)
    console.warn('sessionStorage.serialNo is', sessionStorage.serialNo)
  }

  // 非 Windows 系统不需要证书签名，直接返回 false
  if (!isWindows())
    return Promise.resolve(false)

  // 操作员ID为空时无法初始化证书容器
  if (!operatorId)
    return Promise.resolve(false)

  // 如果缓存中序列号为 -1，（说明没有安装证书）, 则直接返回 false
  if (globalWindow?.sessionStorage?.serialNo === '-1')
    return Promise.resolve(false)

  if (globalWindow?.sessionStorage?.serialNo)
    return Promise.resolve(sessionStorage.serialNo)

  try {
    // 获取加密服务提供者（CSP），检查是否安装了安全组件
    const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
    console.warn('getProviderRes is --', getProviderRes)
    if (!getProviderRes)
      return Promise.resolve(false)

    // 初始化证书容器，使用操作员ID作为用户标识
    signObj.initContr(operatorId)

    // 异步获取证书序列号
    return new Promise((resolve) => {
      signObj.getSerialNoYB((serialNoRes: string) => {
        console.warn('serialNoRes is -', serialNoRes)
        // 当序列号为 -1 或不存在时，统一设置为 -1
        globalWindow.sessionStorage.serialNo = serialNoRes
        resolve(serialNoRes === '-1' ? false : serialNoRes)
      })
    })
  }
  catch (error) {
    // 如果抛出异常，说明当前机器没有安装安全组件或服务未启动
    console.error('获取证书序列号失败:', error)
    return Promise.resolve(false)
  }
}

/**
 * 初始化本地签名环境
 * 检查操作员ID、加密控件是否安装，初始化签名容器
 * @param operatorId 操作员ID
 * @returns Promise<true> 或 Error 对象，代表初始化是否成功
 */
export async function signPreInit(operatorId: string): Promise<true> {
  // 操作员ID为空时直接返回错误
  if (!operatorId)
    return Promise.reject(new Error('操作员ID为空'))

  // 检查安全组件是否安装
  const getProviderRes = await signObj.getProviderAsync('coreCrypto', 0)
  // 调试打印：安全组件状态
  console.warn('getProviderRes is --', getProviderRes)

  if (!getProviderRes)
    return Promise.reject(new Error('未安装安全组件'))

  // 初始化证书容器，绑定操作员ID
  const initRes = signObj.initContr(operatorId)
  // 调试打印：初始化结果
  console.warn('init Res is --', initRes)

  // 返回Promise以支持异步调用
  return Promise.resolve(true)
}
